import { songPublishBadge, type SerializedSongPublish } from 'fym-shared/src/music24/songPublish';
import type { CatalogMasterStatus } from '../../../../redux/api/fymAPI.types';
import type { ServiceExtraActionLook } from '../extras/serviceExtraTypes';
import type { StoreLogin } from '../servicesPanelRows';

/**
 * "SoundCloud extras" (design service-extras a1, a2): the song upload moved out of the row actions
 * of the SoundCloud row into its extras box. One item, "Song". Pure, so it is tested without React.
 *
 * Owner decision 2026-10-18: no admin switch gates the upload. Every artist sees the box; the demo
 * artist sees it with no button.
 */

/** The master of the song: `none` (no file), or the status of the stored master. */
export type SongMasterState = 'none' | CatalogMasterStatus;

/** The state of the item (one sentence each). */
export type SoundCloudExtraState =
  | 'noMaster'
  | 'masterProcessing'
  | 'connect'
  | 'ready'
  | 'waits'
  | 'uploading'
  | 'live'
  | 'failed'
  | 'unsure'
  | 'signedOut'
  | 'removed';

export type SoundCloudExtraChip =
  | 'onReleaseDay'
  | 'goesUp'
  | 'uploading'
  | 'onSoundCloud'
  | 'private'
  | 'failed'
  | 'signedOut'
  | 'removed';

/** What the item can do. Each one is a button or a link of the item. */
export type SoundCloudExtraAction =
  | 'upload'
  | 'connect'
  | 'reconnect'
  | 'change'
  | 'cancel'
  | 'status'
  | 'retry'
  | 'open'
  | 'addMaster';

export interface SoundCloudExtraActionView {
  action: SoundCloudExtraAction;
  look: ServiceExtraActionLook;
}

export interface SoundCloudExtraInput {
  /** The newest SoundCloud record of the song, or null. */
  publish: SerializedSongPublish | null;
  login: StoreLogin;
  master: SongMasterState;
  /** The demo artist: the box with no button (design a1 note). */
  readOnly: boolean;
}

export interface SoundCloudExtraView {
  state: SoundCloudExtraState;
  chip: SoundCloudExtraChip | null;
  actions: SoundCloudExtraActionView[];
  /** The address of the uploaded track (the item shows it and Open opens it). */
  url: string | null;
  /** An action of the artist waits: the box opens by itself (design a0 fold rule). */
  waits: boolean;
}

const LINK = 'link' as const;

function view(
  state: SoundCloudExtraState,
  chip: SoundCloudExtraChip | null,
  actions: SoundCloudExtraActionView[],
  waits: boolean,
  url: string | null = null
): SoundCloudExtraView {
  return { state, chip, actions, url, waits };
}

/** The item with no live, waiting or failed upload: the master, then the login, decide. */
function noUploadView(input: SoundCloudExtraInput): SoundCloudExtraView {
  if (input.master === 'processing') {
    return view('masterProcessing', null, [], false);
  }
  if (input.master !== 'ready') {
    return view('noMaster', null, [{ action: 'addMaster', look: LINK }], true);
  }
  if (input.login === 'expired') {
    return view('signedOut', 'signedOut', [{ action: 'reconnect', look: 'primary' }], true);
  }
  if (input.login === 'none') {
    // Folded: every artist can upload now, so an open "Connect" box on every song of an artist
    // with no SoundCloud would be noise (owner 2026-10-18). The folded line still offers Connect.
    return view('connect', null, [{ action: 'connect', look: 'button' }], false);
  }
  return view('ready', null, [{ action: 'upload', look: 'primary' }], true);
}

function uploadView(input: SoundCloudExtraInput, publish: SerializedSongPublish): SoundCloudExtraView {
  const badge = songPublishBadge(publish);
  if (badge === 'live') {
    const chip = publish.sharing === 'private' ? 'private' : 'onSoundCloud';
    const actions: SoundCloudExtraActionView[] = [{ action: 'status', look: LINK }];
    if (publish.externalUrl !== null) {
      actions.unshift({ action: 'open', look: LINK });
    }
    return view('live', chip, actions, false, publish.externalUrl);
  }
  // A waiting or failed upload with a login that stopped working (design c6 "Signed out").
  if (input.login === 'expired' || (publish.status === 'failed' && publish.failReason === 'reconnect')) {
    return view('signedOut', 'signedOut', [{ action: 'reconnect', look: 'primary' }], true);
  }
  if (badge === 'working') {
    if (publish.status === 'uploading') {
      return view('uploading', 'uploading', [{ action: 'status', look: LINK }], false);
    }
    const chip = publish.timing === 'releaseDay' ? 'onReleaseDay' : 'goesUp';
    return view(
      'waits',
      chip,
      [
        { action: 'change', look: LINK },
        { action: 'cancel', look: LINK },
      ],
      false
    );
  }
  if (publish.status === 'removed') {
    return view(
      'removed',
      'removed',
      [
        { action: 'upload', look: 'primary' },
        { action: 'status', look: LINK },
      ],
      true
    );
  }
  // A break after the file went out (`unsure`) is never sent again by itself: Status says to check
  // SoundCloud first (design c6).
  if (publish.failReason === 'unsure') {
    return view('unsure', 'failed', [{ action: 'status', look: LINK }], true);
  }
  return view(
    'failed',
    'failed',
    [
      { action: 'retry', look: 'primary' },
      { action: 'status', look: LINK },
    ],
    true
  );
}

/** The "Song" item of "SoundCloud extras". */
export function soundCloudExtraView(input: SoundCloudExtraInput): SoundCloudExtraView {
  const { publish } = input;
  const base =
    publish === null || songPublishBadge(publish) === 'none' ? noUploadView(input) : uploadView(input, publish);
  if (!input.readOnly) {
    return base;
  }
  // The demo artist: the state and the Open link only, and the box stays folded.
  return { ...base, actions: base.actions.filter((one) => one.action === 'open'), waits: false };
}

/** The master state of a catalog row (the song summary of the catalog list). */
export function songMasterStateOf(song: {
  hasMaster: boolean;
  masterStatus: CatalogMasterStatus | null;
}): SongMasterState {
  if (!song.hasMaster || song.masterStatus === null) {
    return 'none';
  }
  return song.masterStatus;
}
